import { ProgramGenerationPayload } from 'src/api/mutations/useSummarizeInstructionProd';
import { ProgramStepId, TrainingGoalSlug } from '../types';
import { ProgressItem } from 'src/store/programGeneratorProgressStore';

export const buildPayloadFromProgress = (
  progress: Record<string, ProgressItem[]>,
  clientId: string | null,
): ProgramGenerationPayload | null => {
  if (!clientId || !progress) return null;

  const getValues = (stepId: ProgramStepId) => (progress[stepId] || []).map((p) => p.slug);

  const training_goal = getValues(ProgramStepId.TrainingGoal);
  const goal = training_goal[0];

  if (!goal) return null; // We need at least the main goal to build a valid payload

  let dynamicParams = {};
  if (goal === TrainingGoalSlug.IncreaseStrength) {
    dynamicParams = {
      training_goal_details: getValues(ProgramStepId.IncreaseStrengthFocus),
      training_goal_preferences: getValues(ProgramStepId.IncreaseStrengthOutcome),
      training_goal_approaches: getValues(ProgramStepId.IncreaseStrengthApproach),
    };
  } else if (goal === TrainingGoalSlug.BuildMuscle) {
    dynamicParams = {
      training_goal_details: getValues(ProgramStepId.BuildMusclePrimary),
      training_goal_preferences: getValues(ProgramStepId.BuildMuscleSecondary),
      training_goal_approaches: getValues(ProgramStepId.BuildMuscleApproach),
    };
  } else if (goal === TrainingGoalSlug.WeightLoss) {
    dynamicParams = {
      training_goal_details: getValues(ProgramStepId.WeightLossPrimary),
      training_goal_preferences: getValues(ProgramStepId.WeightLossSecondary),
      training_goal_approaches: getValues(ProgramStepId.WeightLossApproach),
    };
  } else if (goal === TrainingGoalSlug.Endurance) {
    dynamicParams = {
      training_goal_details: getValues(ProgramStepId.EnduranceType),
      training_goal_approaches: getValues(ProgramStepId.EnduranceApproach),
    };
  } else if (goal === TrainingGoalSlug.Mobility) {
    dynamicParams = {
      training_goal_details: getValues(ProgramStepId.MobilityArea),
      training_goal_preferences: getValues(ProgramStepId.MobilityPurpose),
      training_goal_approaches: getValues(ProgramStepId.MobilityApproach),
    };
  } else if (goal === TrainingGoalSlug.Performance) {
    dynamicParams = {
      training_goal_details: getValues(ProgramStepId.PerformanceSupport),
      training_goal_preferences: getValues(ProgramStepId.PerformanceFocus),
      training_goal_approaches: getValues(ProgramStepId.PerformanceApproach),
    };
  } else if (goal === TrainingGoalSlug.GeneralFitness) {
    dynamicParams = {
      training_goal_details: getValues(ProgramStepId.GeneralFitnessMeaning),
      training_goal_approaches: getValues(ProgramStepId.GeneralFitnessApproach),
    };
  } else if (goal === TrainingGoalSlug.Rehabilitation) {
    dynamicParams = {
      training_goal_details: getValues(ProgramStepId.RehabilitationArea),
      training_goal_preferences: getValues(ProgramStepId.RehabilitationStage),
      training_goal_approaches: getValues(ProgramStepId.RehabilitationApproach),
    };
  } else if (goal === TrainingGoalSlug.InjuryPrevention) {
    dynamicParams = {
      training_goal_details: getValues(ProgramStepId.InjuryPreventionArea),
      training_goal_preferences: getValues(ProgramStepId.InjuryPreventionFocus),
      training_goal_approaches: getValues(ProgramStepId.InjuryPreventionApproach),
    };
  }

  return {
    clientId: clientId,
    parameters: {
      energy_level: getValues(ProgramStepId.EnergyLevel),
      non_gym_activity: getValues(ProgramStepId.ActivityLevel),
      fitness_level: getValues(ProgramStepId.FitnessLevel),
      movement_insights: getValues(ProgramStepId.MovementInsights),
      injuries_limitations:
        getValues(ProgramStepId.InjuriesParts).length > 0
          ? getValues(ProgramStepId.InjuriesParts)
          : ['None'],
      program_duration: getValues(ProgramStepId.ProgramDuration),
      training_schedule:
        getValues(ProgramStepId.HowOftenTrain).length > 0
          ? [getValues(ProgramStepId.HowOftenTrain).join(', ')]
          : getValues(ProgramStepId.HowOftenTrain),
      workout_duration: getValues(ProgramStepId.WorkoutDuration),
      training_goal: training_goal,
      secondary_training_goals:
        getValues(ProgramStepId.SecondaryGoals).length > 0
          ? getValues(ProgramStepId.SecondaryGoals)
          : ['None'],
      program_intensity: getValues(ProgramStepId.Intensity),
      gym_equipments:
        getValues(ProgramStepId.SelectedEquipment).length > 0
          ? getValues(ProgramStepId.SelectedEquipment)
          : ['None'],
      weight_loss_target:
        goal === TrainingGoalSlug.WeightLoss
          ? getValues(ProgramStepId.WeightLossTarget)
          : undefined,
      rehabilitation_stage:
        goal === TrainingGoalSlug.Rehabilitation
          ? getValues(ProgramStepId.RehabilitationStage)
          : undefined,
      ...dynamicParams,
    },
  };
};
